import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getDiseasesAndCropName,
  getDiseaseFullDetails,
  renderSymptoms,
  renderAdviceList,
  renderImportantNotes
} from '../utils/diseaseUtils';
import NoImageAvailable from '../assets/images/No_Image_Available.jpg';

const DiagnosticToolPage = () => {
  const { t } = useTranslation();
  const { cid } = useParams();
  const navigate = useNavigate();

  const [issues, setIssues] = useState([]);
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [diseaseDetails, setDiseaseDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [isImportantNoteExpanded, setIsImportantNoteExpanded] = useState(false);
  const [cropName, setCropName] = useState('');
  const [currentView, setCurrentView] = useState('list');
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Track page refresh state
  useEffect(() => {
    if (window.performance && window.performance.getEntriesByType) {
      const navigationEntries = window.performance.getEntriesByType('navigation');
      if (navigationEntries.length > 0) {
        setIsRefreshing(navigationEntries[0].type === 'reload');
      } else if (performance.navigation) {
        setIsRefreshing(performance.navigation.type === performance.navigation.TYPE_RELOAD);
      }
    }
  }, []);

  // Main data loading effect
  useEffect(() => {
    const loadData = async () => {
      try {
        const { diseases, cropName } = await getDiseasesAndCropName(cid);
        setIssues(diseases);
        setCropName(cropName);
        
        const storedDiseaseId = sessionStorage.getItem(`selectedDisease_${cid}`);
        const storedView = sessionStorage.getItem(`currentView_${cid}`);
        
        if (storedDiseaseId && storedView === 'details') {
          const selectedIssue = diseases.find(disease => 
            disease.id === storedDiseaseId || 
            disease.diseaseName === storedDiseaseId ||
            JSON.stringify(disease) === storedDiseaseId
          );
          
          if (selectedIssue) {
            setCurrentView('details');
            await handleCheckAdvice(selectedIssue, false);
          }
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (cid) loadData();

    // Cleanup on unmount or navigation
    return () => {
      if (cid && !isRefreshing) {
        sessionStorage.removeItem(`selectedDisease_${cid}`);
        sessionStorage.removeItem(`currentView_${cid}`);
      }
    };
  }, [cid, isRefreshing]);

  const handleCheckAdvice = async (issue, shouldStore = true) => {
    setDetailsLoading(true);
    try {
      const { selectedDisease, diseaseDetails, cropName: newCropName } = await getDiseaseFullDetails(issue, cropName);
      setSelectedDisease(selectedDisease);
      setDiseaseDetails(diseaseDetails);
      setCropName(newCropName);
      setCurrentView('details');
      
      // Store the selection to maintain state on refresh
      if (shouldStore && cid) {
        const diseaseId = issue.id || issue.diseaseName || JSON.stringify(issue);
        sessionStorage.setItem(`selectedDisease_${cid}`, diseaseId);
        sessionStorage.setItem(`currentView_${cid}`, 'details');
      }
    } catch (error) {
      console.error('Error fetching disease details:', error);
      alert('Failed to fetch disease details.');
    } finally {
      setDetailsLoading(false);
    }
  };
  

  const handleBackToList = () => {
    setSelectedDisease(null);
    setDiseaseDetails(null);
    setCurrentView('list');
    
    // Clear stored selection
    if (cid) {
      sessionStorage.removeItem(`selectedDisease_${cid}`);
      sessionStorage.setItem(`currentView_${cid}`, 'list');
    }
  };

  const handleStartFresh = () => {
    setSelectedDisease(null);
    setDiseaseDetails(null);
    setCurrentView('list');
    
    // Clear stored selection
    if (cid) {
      sessionStorage.removeItem(`selectedDisease_${cid}`);
      sessionStorage.removeItem(`currentView_${cid}`);
    }
    
    navigate('/');
  };

  const handleSaveAsImage = () => {
    window.print();
  };

  const handleSaveAsPDF = () => {
    window.print();
  };

  if (selectedDisease && diseaseDetails) {
    const images = diseaseDetails.images || [];

    return (
      <div className="min-h-screen w-full bg-gray-50">
        <div className="bg-white shadow-sm w-full px-4 py-4 flex justify-between">
          <button onClick={handleBackToList} className="text-blue-600 font-medium hover:text-blue-800"> {t('common.back')}</button>
          <button onClick={handleStartFresh} className="text-blue-600 font-medium hover:text-blue-800">{t('common.startFresh')}</button>
        </div>

        <div className="w-full px-4 py-8">
          <h1 className="text-2xl font-bold text-center text-gray-800 mb-4">{cropName} {t('diagnostic.cropProblem')}</h1>
          <div className="text-center bg-gray-100 rounded-lg p-6 max-w-6xl mx-auto mb-8 text-xl">
            <span className="font-medium">{t('diagnostic.problemName')}: </span>
            <span className="text-red-600 font-semibold">{diseaseDetails.chemical_name || selectedDisease.diseaseName}</span>
          </div>

          <div className="max-w-6xl mx-auto">
            {/* Sample Images */}
            {/* <section className="mb-8">
              <h2 className="text-xl font-semibold text-center mb-4">{t('diagnostic.sampleImages')}</h2>
              {images.length > 0 ? (
                <div className="flex flex-wrap justify-center gap-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-4">
                    {images.map((url, i) => (
                      <div key={i} className="aspect-square w-full">
                        <img
                          src={url}
                          alt={t('diagnostic.sampleImageAlt', { number: i + 1 })}
                          className="aspect-square w-full object-cover rounded-lg shadow-sm"
                          onError={(e) => {
                            e.target.src = NoImageAvailable;
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <img
                    src={NoImageAvailable}
                    alt={t('diagnostic.noImages')}
                    className="aspect-square w-48 h-48 rounded-lg shadow-sm border"
                  />
                </div>
              )}
            </section> */}
            {/* Sample Images */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-center mb-4">{t('diagnostic.sampleImages')}</h2>
              {images.length > 0 ? (
                <div className="max-w-6xl mx-auto">
                  <div className="flex flex-wrap justify-center gap-4">
                    {images.map((url, i) => (
                      <img
                        key={i}
                        src={url}
                        alt={t('diagnostic.sampleImageAlt', { number: i + 1 })}
                        className="w-24 h-24 md:w-32 md:h-32 object-cover rounded-lg shadow-sm"
                        onError={(e) => {
                          e.target.src = NoImageAvailable;
                        }}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <img
                    src={NoImageAvailable}
                    alt={t('diagnostic.noImages')}
                    className="aspect-square w-48 h-48 rounded-lg shadow-sm border"
                  />
                </div>
              )}
            </section>

            {/* Chemical Advice */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-center mb-4">{t('diagnostic.chemicalAdvice')}</h2>
              <div className="bg-white rounded-lg p-6 shadow-sm text-gray-700">
                {renderAdviceList(diseaseDetails.chemicalAdvice || diseaseDetails.chemical_advice) || (
                  <p className="text-center text-gray-500">{t('diagnostic.noChemicalAdvice')}</p>
                )}
              </div>
            </section>

            {/* Organic/IPM Measures */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-center mb-4">{t('diagnostic.organicAdvice')}</h2>
              <div className="bg-white rounded-lg p-6 shadow-sm text-gray-700">
                {renderAdviceList(diseaseDetails.organiceAdvice || diseaseDetails.organic_advice || diseaseDetails.ipmMeasures) || (
                  <p className="text-center text-gray-500">{t('diagnostic.noOrganicAdvice')}</p>
                )}
              </div>
            </section>

            {/* Symptoms */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-center mb-4">{t('diagnostic.symptoms')}</h2>
              <div className="bg-white rounded-lg p-6 shadow-sm text-gray-700">
                {renderSymptoms(diseaseDetails.symptoms || diseaseDetails.disease_symptoms) || (
                  <p className="text-center text-gray-500">{t('diagnostic.noSymptoms')}</p>
                )}
              </div>
            </section>

            {/* Important Notes */}
            <section className="mb-8">
              <div
                className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded cursor-pointer hover:bg-yellow-100"
                onClick={() => setIsImportantNoteExpanded(!isImportantNoteExpanded)}
              >
                <div className="flex justify-between">
                  <span className="text-sm font-semibold text-yellow-700">▼ {t('diagnostic.importantNote')}</span>
                  <span className="text-lg text-yellow-700">{isImportantNoteExpanded ? '−' : '+'}</span>
                </div>
                {isImportantNoteExpanded && (
                  <div className="text-sm text-yellow-700 mt-4 space-y-2">
                    {renderImportantNotes([
                      t('diagnostic.notes.note1'),
                      t('diagnostic.notes.note2'),
                      t('diagnostic.notes.note3'),
                      t('diagnostic.notes.note4'),
                      t('diagnostic.notes.note5')
                    ])}
                  </div>
                )}
              </div>
            </section>

            {/* Bottom Buttons */}
            <div className="flex justify-center space-x-4">
              <button onClick={handleSaveAsImage} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700">{t('diagnostic.saveAsImage')}</button>
              <button onClick={handleSaveAsPDF} className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700">{t('diagnostic.saveAsPdf')}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default list view
  return (
    <div className="min-h-screen w-full bg-gray-50">
      <div className="bg-white shadow-sm w-full px-4 py-4 flex justify-between">
        <button onClick={() => navigate('/')} className="text-blue-600 font-medium hover:text-blue-800"> {t('common.back')}</button>
        <button onClick={handleStartFresh} className="text-blue-600 font-medium hover:text-blue-800">{t('common.startFresh')}</button>
      </div>

      {cropName && (
        <div className="w-full px-4 py-4 bg-white border-b">
          <h1 className="text-xl font-bold text-center text-gray-800">{cropName} - {t('diagnostic.diseaseList')}</h1>
        </div>
      )}

      <div className="w-full px-4 py-8">
        {loading ? (
          <p className="text-center text-gray-600">{t('diagnostic.loading')}</p>
        ) : issues.length === 0 ? (
          <p className="text-center text-gray-600">{t('diagnostic.noDiseases')}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {issues.map((issue, i) => (
              <div key={i} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg">
                <img
                  src={issue.imageUrl?.[0] || NoImageAvailable}
                  alt={t('diagnostic.diseaseImageAlt')}
                  className="w-full h-48 object-cover"
                  onError={(e) => {
                    e.target.src = NoImageAvailable;
                  }}
                />
                <div className="p-4 flex flex-col items-center">
                  <h2 className="text-center font-medium mb-2 text-gray-800">
                    {issue.chemical_name || issue.scientific_name || issue.diseaseName || t('diagnostic.unknownDisease')}
                  </h2>
                  <button
                    onClick={() => handleCheckAdvice(issue)}
                    disabled={detailsLoading}
                    className="bg-green-600 text-white px-4 py-2 rounded w-full hover:bg-green-700 disabled:opacity-50"
                  >
                    {detailsLoading ? t('diagnostic.loading') : t('diagnostic.checkAdvice')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticToolPage;